/**
 * <cart-drawer-suggestions> — suggestions du tiroir panier pilotées par le
 * montant manquant avant la livraison gratuite, à partir du CATALOGUE COMPLET.
 *
 * Pourquoi côté client : Liquid plafonne toute collection à 50 produits et
 * n'expose pas la collection automatique "all" — impossible d'avoir le
 * catalogue entier côté serveur. Ici on le télécharge une fois via
 * /collections/all/products.json (paginé), on le met en cache
 * (sessionStorage + mémoire, TTL 30 min) et on recalcule à chaque re-render
 * du drawer : le serveur re-rend ce custom element avec les data-attributes
 * frais (montant manquant, produits au panier) après CHAQUE opération
 * panier, donc connectedCallback rejoue la sélection sans autre plomberie.
 *
 * Exclusions : produits du panier, épuisés, cartes-cadeaux / produits sans
 * expédition, et tag "exclu-suggestions" (levier marchand sans code).
 *
 * Localisation : le fetch du catalogue ET tous les liens produits passent
 * par Shopify.routes.root (défini dans layout/theme.liquid — "/" en
 * français, "/en/" en anglais). Indispensable au-delà du simple préfixe
 * d'URL : l'endpoint products.json localisé renvoie titres ET handles
 * traduits (ex. "tote-bag-beige" sur /en vs "fourre-tout-beige"), donc un
 * catalogue fetché sans préfixe donnerait des cartes en français sur la
 * version anglaise. Le cache est par conséquent séparé par locale.
 *
 * Tri : passe 1 = prix ≥ montant manquant, croissant (le plus proche
 * au-dessus en premier) ; passe 2 = prix < montant manquant, décroissant.
 * L'état "seuil atteint" reste servi par le rendu serveur générique
 * (snippets/cart-drawer-recommendations.liquid) — cet element ne se rend
 * que sous le seuil.
 */
if (!customElements.get('cart-drawer-suggestions')) {
  class CartDrawerSuggestions extends HTMLElement {
    static CACHE_KEY = 'ciao:suggestions-catalog:v1';
    static CACHE_TTL_MS = 30 * 60 * 1000;
    static EXCLUDE_TAG = 'exclu-suggestions';
    static MAX_SLIDES = 8;
    static catalogPromise = null;

    /* Racine localisée avec slash final garanti ("/", "/en/") — tolère un
       Shopify.routes.root absent ou sans slash final. */
    static root() {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return root.endsWith('/') ? root : `${root}/`;
    }

    connectedCallback() {
      this.remainingCents = parseInt(this.dataset.remainingCents, 10) || 0;
      this.cartProductIds = (this.dataset.cartProductIds || '')
        .split(',')
        .filter(Boolean)
        .map(Number);
      this.list = this.querySelector('.cart-drawer-recommendations__list');
      if (!this.list || this.remainingCents <= 0) return;
      CartDrawerSuggestions.loadCatalog()
        .then((products) => this.render(products))
        .catch((error) => console.error('[suggestions-panier] catalogue indisponible :', error));
    }

    static loadCatalog() {
      if (this.catalogPromise) return this.catalogPromise;
      const cacheKey = `${this.CACHE_KEY}:${this.root()}`;
      try {
        const cached = JSON.parse(sessionStorage.getItem(cacheKey));
        if (cached && Date.now() - cached.ts < this.CACHE_TTL_MS && Array.isArray(cached.products)) {
          this.catalogPromise = Promise.resolve(cached.products);
          return this.catalogPromise;
        }
      } catch (error) {
        /* cache illisible : on re-télécharge */
      }
      this.catalogPromise = (async () => {
        const products = [];
        // Garde-fou pagination : 10 pages × 250 = 2500 produits max.
        for (let page = 1; page <= 10; page++) {
          const response = await fetch(`${this.root()}collections/all/products.json?limit=250&page=${page}`);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const batch = (await response.json()).products || [];
          products.push(...batch.map(CartDrawerSuggestions.slim));
          if (batch.length < 250) break;
        }
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), products }));
        } catch (error) {
          /* stockage plein/indisponible : le cache mémoire suffit */
        }
        return products;
      })();
      // Un échec réseau ne doit pas empoisonner le cache : retry possible.
      this.catalogPromise.catch(() => (CartDrawerSuggestions.catalogPromise = null));
      return this.catalogPromise;
    }

    // Ne garde du JSON produit que ce dont la carte a besoin (le cache reste léger).
    static slim(product) {
      const variants = (product.variants || []).map((variant) => ({
        id: variant.id,
        available: !!variant.available,
        priceCents: Math.round(parseFloat(variant.price) * 100),
        compareAtCents: variant.compare_at_price
          ? Math.round(parseFloat(variant.compare_at_price) * 100)
          : null,
        requiresShipping: variant.requires_shipping !== false,
      }));
      const buyable = variants.filter((variant) => variant.available);
      const priceCents = (buyable.length ? buyable : variants).reduce(
        (min, variant) => Math.min(min, variant.priceCents),
        Infinity
      );
      return {
        id: product.id,
        title: product.title,
        handle: product.handle,
        type: product.product_type || '',
        tags: product.tags || [],
        image: (product.images && product.images[0] && product.images[0].src) || null,
        variants,
        priceCents: Number.isFinite(priceCents) ? priceCents : 0,
      };
    }

    candidates(products) {
      return products.filter((product) => {
        if (this.cartProductIds.includes(product.id)) return false;
        if (!product.variants.some((variant) => variant.available)) return false;
        if (product.tags.some((tag) => tag.toLowerCase() === CartDrawerSuggestions.EXCLUDE_TAG)) return false;
        // Cartes-cadeaux : type "Gift Cards" ou produit sans expédition.
        if (/gift.?card|carte-cadeau/i.test(`${product.type} ${product.handle}`)) return false;
        if (product.variants.every((variant) => !variant.requiresShipping)) return false;
        return true;
      });
    }

    select(pool) {
      const above = pool
        .filter((product) => product.priceCents >= this.remainingCents)
        .sort((a, b) => a.priceCents - b.priceCents);
      const below = pool
        .filter((product) => product.priceCents < this.remainingCents)
        .sort((a, b) => b.priceCents - a.priceCents);
      return above.concat(below).slice(0, CartDrawerSuggestions.MAX_SLIDES);
    }

    render(products) {
      const pool = this.candidates(products);
      const ordered = this.select(pool);
      if (!ordered.length) return;
      this.list.innerHTML = ordered.map((product, index) => this.slideHtml(product, index)).join('');
      this.removeAttribute('hidden');
      const slider = this.querySelector('slider-component');
      if (slider && typeof slider.resetPages === 'function') slider.resetPages();
    }

    slideHtml(product, index) {
      const productUrl = `${CartDrawerSuggestions.root()}products/${this.escape(product.handle)}`;
      return `
        <li id="Slide-${this.dataset.sectionId}-${index + 1}" class="cart-drawer-recommendations__item slider__slide">
          <div class="cart-drawer-product-card">
            <a href="${productUrl}" class="cart-drawer-product-card__media" tabindex="-1" aria-hidden="true">
              ${this.imageHtml(product)}
            </a>
            <div class="cart-drawer-product-card__info">
              <a href="${productUrl}" class="cart-drawer-product-card__title" id="title-${this.dataset.sectionId}-${product.id}">${this.escape(product.title)}</a>
              ${this.priceHtml(product)}
            </div>
            ${this.buttonHtml(product)}
          </div>
        </li>`;
    }

    imageHtml(product) {
      if (!product.image) return '';
      const sep = product.image.includes('?') ? '&' : '?';
      const src = this.escape(product.image);
      return `<img src="${src}${sep}width=150" srcset="${src}${sep}width=150 150w, ${src}${sep}width=300 300w" sizes="7.2rem" alt="${this.escape(product.title)}" loading="lazy" width="150" height="150">`;
    }

    priceHtml(product) {
      const variant =
        product.variants.find((item) => item.available && item.priceCents === product.priceCents) ||
        product.variants[0];
      const money = (cents) => this.formatMoney(cents);
      if (variant.compareAtCents && variant.compareAtCents > variant.priceCents) {
        return `<div class="price price--on-sale"><div class="price__container"><div class="price__sale"><s class="price-item price-item--regular">${money(variant.compareAtCents)}</s> <span class="price-item price-item--sale price-item--last">${money(variant.priceCents)}</span></div></div></div>`;
      }
      return `<div class="price"><div class="price__container"><div class="price__regular"><span class="price-item price-item--regular">${money(variant.priceCents)}</span></div></div></div>`;
    }

    buttonHtml(product) {
      // Multi-variantes : lien direct vers la page produit (pas de popup),
      // même règle que la carte Liquid (cart-drawer-product-card.liquid).
      if (product.variants.length > 1) {
        return `<a href="${CartDrawerSuggestions.root()}products/${this.escape(product.handle)}" class="button cart-drawer-product-card__button" aria-describedby="title-${this.dataset.sectionId}-${product.id}">${this.escape(this.dataset.labelChoose)}</a>`;
      }
      const variant = product.variants[0];
      const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
      return `
        <product-form data-hide-errors="true">
          <form action="${addUrl}" method="post" enctype="multipart/form-data" novalidate="novalidate" data-type="add-to-cart-form">
            <input type="hidden" name="id" value="${variant.id}">
            <button type="submit" name="add" class="button cart-drawer-product-card__button" aria-describedby="title-${this.dataset.sectionId}-${product.id}">
              <span>${this.escape(this.dataset.labelAdd)}</span>
              <span class="sold-out-message hidden">${this.escape(this.dataset.labelSoldout)}</span>
              <div class="loading__spinner hidden">
                <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg>
              </div>
            </button>
          </form>
        </product-form>`;
    }

    /* Reproduit le filtre `money` de Liquid à partir du gabarit
       shop.money_format de la boutique (ex. "${{amount}}"), passé via
       data-money-format par snippets/cart-drawer-recommendations.liquid.
       Intl.NumberFormat donnait « 170,00 $CA » en fr-CA là où tout le
       reste du tiroir (lignes du panier, totaux) rend « $170.00 » — même
       gabarit partout, dans les deux langues. */
    formatMoney(cents) {
      const format = this.dataset.moneyFormat || '${{amount}}';
      const amount = (precision, thousands, decimal) => {
        const [units, fraction] = (cents / 100).toFixed(precision).split('.');
        const grouped = units.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return fraction ? grouped + decimal + fraction : grouped;
      };
      return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (placeholder, key) => {
        switch (key) {
          case 'amount_no_decimals':
            return amount(0, ',', '.');
          case 'amount_with_comma_separator':
            return amount(2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return amount(0, '.', ',');
          case 'amount_with_apostrophe_separator':
            return amount(2, "'", '.');
          case 'amount_with_space_separator':
            return amount(2, ' ', ',');
          case 'amount_no_decimals_with_space_separator':
            return amount(0, ' ', ',');
          default:
            return amount(2, ',', '.');
        }
      });
    }

    escape(text) {
      const div = document.createElement('div');
      div.textContent = text == null ? '' : String(text);
      return div.innerHTML.replace(/"/g, '&quot;');
    }
  }

  customElements.define('cart-drawer-suggestions', CartDrawerSuggestions);
}